import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Popover } from '@material-ui/core';
import { useIntl } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';
import { SimpleAutocomplete } from '~/modules/common/components/SearchAutocomplete/SimpleAutocomplete';
import { useAvailableCriteria } from './hooks';

const usePopoverStyles = makeStyles(theme => ({
  paper: {
    minWidth: 350,
    maxWidth: 400,
    padding: theme.spacing(0.5),
    marginTop: theme.spacing(-5)
  }
}));

const useAutocompleteStyles = makeStyles(() => ({
  popper: {
    position: 'relative !important',
    transform: 'none !important',
    boxShadow: 'none !important',
    marginTop: 0
  },
  paper: {
    boxShadow: 'none !important',
    margin: 0
  }
}));

const popoverAnchorOrigin = {
  vertical: 'bottom',
  horizontal: 'left'
};

const popoverTransformOrigin = {
  vertical: 'top',
  horizontal: 'left'
};

const AddCriteriaMenu = ({
  anchorEl,
  open,
  onClose,
  onSelect,
  selectedTypes
}) => {
  const intl = useIntl();
  const popoverClasses = usePopoverStyles();
  const autocompleteClasses = useAutocompleteStyles();

  const availableCriteriaTypes = useAvailableCriteria();

  const handleSelect = useCallback(
    selectedOption => {
      if (selectedOption) {
        onSelect(selectedOption.type);
        onClose();
      }
    },
    [onSelect, onClose]
  );

  const availableOptions = useMemo(
    () =>
      availableCriteriaTypes
        .filter(criteriaType => !selectedTypes.includes(criteriaType.type))
        .map(criteriaType => ({
          ...criteriaType,
          displayText: intl.formatMessage({ id: criteriaType.labelId })
        })),
    [availableCriteriaTypes, selectedTypes, intl]
  );

  const autocompleteClassesProp = useMemo(
    () => ({
      popper: autocompleteClasses.popper,
      paper: autocompleteClasses.paper
    }),
    [autocompleteClasses.popper, autocompleteClasses.paper]
  );

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      classes={popoverClasses}
      anchorOrigin={popoverAnchorOrigin}
      transformOrigin={popoverTransformOrigin}
    >
      <SimpleAutocomplete
        autoFocus
        openOnFocus
        disablePortal
        value={null}
        onChange={handleSelect}
        options={availableOptions}
        optionPropText="displayText"
        noOptionsText={intl.formatMessage({
          id: 'moveTimeDialog.noCriteriaAvailable'
        })}
        fullWidth
        variant="outlined"
        ariaLabel={intl.formatMessage({
          id: 'moveTimeDialog.addCriteria'
        })}
        classes={autocompleteClassesProp}
      />
    </Popover>
  );
};

AddCriteriaMenu.propTypes = {
  anchorEl: PropTypes.object,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  selectedTypes: PropTypes.arrayOf(PropTypes.string).isRequired
};

export default AddCriteriaMenu;
